import type { BaseLanguageType } from '@dvb/common';
import type { ButtonProps } from '@heroui/react';

import { BaseLanguageList, LOCAL_STORAGE_KEY_LANG, Logger } from '@dvb/common';
import { faLanguage } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  isIconOnly?: boolean;
  onSelect?: (type: BaseLanguageType) => void;
  size?: ButtonProps['size'];
}

export function LanguageMenu({
  isIconOnly = false,
  onSelect = undefined,
  size = 'sm',
}: Props) {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const selectLanguage = async (type: BaseLanguageType) => {
    try {
      setIsLoading(true);
      await i18n.changeLanguage(type);
      onSelect?.(type);

      // Use raw localStorage.setItem to prevent stringify the value
      localStorage.setItem(LOCAL_STORAGE_KEY_LANG, type);
    } catch (error) {
      Logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dropdown className="min-w-[150px]">
      <DropdownTrigger>
        {isIconOnly ? (
          // Used in IOSInstallGuideModal and AndroidInstallGuideModal
          <Button isIconOnly isLoading={isLoading} size={size} variant="flat">
            <FontAwesomeIcon icon={faLanguage} />
          </Button>
        ) : (
          <Button color="primary" isLoading={isLoading} size={size}>
            {BaseLanguageList.find((item) => item.key === i18n.language)?.name}
          </Button>
        )}
      </DropdownTrigger>
      <DropdownMenu
        aria-labelledby={i18n.language}
        disallowEmptySelection
        onSelectionChange={(key) => {
          selectLanguage([...(key as Set<BaseLanguageType>)][0]);
        }}
        selectedKeys={new Set([i18n.language])}
        selectionMode="single"
        variant="flat"
      >
        {BaseLanguageList.map((item) => (
          <DropdownItem className="py-3" key={item.key}>
            {item.name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
